import type { ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import { getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import type { TripDay, TripItineraryItem, TripItemWithoutDay } from 'tripPlanner/api/types'

interface Props {
  day: TripDay
  items: Array<TripItineraryItem>
  expanded?: boolean
  onToggle?: (dayNumber: number) => void
  secondaryAction?: ReactNode
}

interface ItinerarySummaryRowProps {
  indicator: ReactNode
  title: string
  subtitle: string
  previewImageIds: Array<string>
  extraImageCount: number
  expanded?: boolean
  onClick?: () => void
  secondaryAction?: ReactNode
}

const SummaryRowContainer = styled.div`
  align-items: center;
  display: flex;
  gap: ${rem(12)};
  width: 100%;
`

const DatePill = styled.div`
  align-items: center;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.round};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  display: flex;
  flex-shrink: 0;
  font-size: ${rem(14)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  justify-content: center;
  line-height: ${rem(20)};
  padding: ${rem(6)} ${rem(12)};
  transition: background 120ms ease;
  white-space: nowrap;
`

const SummaryRow = styled(Clickable)<{ $expanded?: boolean }>`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: ${props => props.theme.borderRadius.S};
  display: flex;
  flex: 1;
  gap: ${rem(12)};
  justify-content: space-between;
  min-width: 0;
  padding: ${rem(12)} 0;
  text-align: start;
  transition: background 120ms ease;

  ${props => !props.$expanded && `
    &:hover {
      background: ${props.theme.palette.neutral.surface.lightBackground};
    }

    &:hover ${DatePill} {
      background: ${props.theme.palette.neutral.default.six};
    }
  `}
`

const SummaryText = styled.div`
  flex: 1;
  min-width: 0;
`

const PreviewStack = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: ${rem(4)};
`

const PreviewTile = styled.div`
  border-radius: ${props => props.theme.borderRadius.L};
  height: var(--itinerary-preview-tile-size, ${rem(40)});
  overflow: hidden;
  position: relative;
  width: var(--itinerary-preview-tile-size, ${rem(40)});
`

const PreviewImage = styled(Image)`
  height: 100%;
  width: 100%;

  img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`

const ExtraCountOverlay = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  display: flex;
  height: 100%;
  inset: 0;
  justify-content: center;
  position: absolute;
  width: 100%;
`

const PreviewChevron = styled.div`
  align-items: center;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  display: flex;
  flex-shrink: 0;
  justify-content: center;
`

function getItemImageId(item: TripItineraryItem | TripItemWithoutDay): string | undefined {
  const savedItemData = item.savedItemData
  const itemImageIds = savedItemData && 'imageIds' in savedItemData ? savedItemData.imageIds : undefined

  return itemImageIds?.[0] ?? item.itemPlaceDetail?.imageIds?.[0]
}

function getPreviewImages(items: Array<TripItineraryItem | TripItemWithoutDay>): Array<string> {
  const previewImages: Array<string> = []

  for (const item of items) {
    const imageId = getItemImageId(item)

    if (!imageId) {
      continue
    }

    previewImages.push(imageId)

    if (previewImages.length === 4) {
      break
    }
  }

  return previewImages
}

function ItinerarySummaryRow(props: ItinerarySummaryRowProps) {
  const { indicator, title, subtitle, previewImageIds, extraImageCount, expanded, onClick, secondaryAction } = props
  const intl = useIntl()

  const visiblePreviewImageIds = extraImageCount > 0 ? previewImageIds.slice(0, previewImageIds.length - 1) : previewImageIds
  const lastPreviewImageId = previewImageIds[previewImageIds.length - 1]

  return (
    <SummaryRowContainer>
      <SummaryRow $expanded={expanded} onClick={onClick}>
        <Group direction="horizontal" gap={12} verticalAlign="center" fullWidth>
          <PreviewChevron>
            <DropdownChevron direction={expanded ? 'down' : 'right'} size="S" />
          </PreviewChevron>
          {indicator}
          {!expanded && (
            <SummaryText>
              <BodyText variant="medium" weight="bold">{title}</BodyText>
              <Caption variant="large" colour="light-foreground-alt">{subtitle}</Caption>
            </SummaryText>
          )}
        </Group>
        {!expanded && (
          <PreviewStack>
            {visiblePreviewImageIds.map((imageId, index) => (
              <PreviewTile key={`${imageId}-${index}`}>
                <PreviewImage id={imageId} fit="center" />
              </PreviewTile>
            ))}
            {extraImageCount > 0 && lastPreviewImageId && (
              <PreviewTile>
                <PreviewImage id={lastPreviewImageId} fit="center" />
                <ExtraCountOverlay>
                  <BodyText variant="medium" weight="normal">
                    {intl.formatMessage(
                      {
                        id: 'aJKFcD79BqWApadMvSK7',
                        defaultMessage: '+{extraImageCount}',
                        description: 'Overlay label showing how many additional itinerary preview images are not displayed',
                      },
                      { extraImageCount },
                    )}
                  </BodyText>
                </ExtraCountOverlay>
              </PreviewTile>
            )}
          </PreviewStack>
        )}
      </SummaryRow>
      {secondaryAction}
    </SummaryRowContainer>
  )
}

function ItineraryContent(props: Props) {
  const { day, items, expanded, onToggle, secondaryAction } = props
  const intl = useIntl()

  const activityCountLabel = items.length === 0 ?
      intl.formatMessage({
        id: '-kHcv34-127fl39BLlqu',
        defaultMessage: 'Nothing planned yet',
        description: 'Empty item count label shown in a condensed itinerary day summary row',
      }) :
      intl.formatMessage(
        {
          id: 'InX6e1dMtUFt3ENo0921',
          defaultMessage: '{count, plural, one {# item} other {# items}}',
          description: 'Item count label shown in a condensed itinerary day summary row',
        },
        { count: items.length },
      )
  const previewImageIds = useMemo(() => getPreviewImages(items), [items])
  const extraImageCount = Math.max(items.length - previewImageIds.length, 0)

  const handleToggleClick = useCallback(() => {
    onToggle?.(day.dayNumber)
  }, [day.dayNumber, onToggle])

  return (
    <ItinerarySummaryRow
      indicator={<DatePill>{getLocalisedDayLabel(intl, day.dayNumber, day.date)}</DatePill>}
      title={day.title}
      subtitle={activityCountLabel}
      previewImageIds={previewImageIds}
      extraImageCount={extraImageCount}
      expanded={expanded}
      onClick={handleToggleClick}
      secondaryAction={secondaryAction}
    />
  )
}

export { ItinerarySummaryRow, DatePill, getPreviewImages }
export default ItineraryContent
